import GuestRoutes from 'routes/guest'
import useCreateApollo from 'lib/apollo'
import { ApolloProvider } from '@apollo/client'
import { ThemeProvider } from 'styled-components'
import theme from 'lib/theme'
import { BrowserRouter, useLocation } from 'react-router-dom'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { cleanAuthData, getAuthData, setToken } from 'utils/auth'
import { noop } from 'lodash-es'
import Popups from 'components/Popups'
import AdminRoutes from 'routes/admin'
import ClientRoutes from 'routes/client'
import ModelRoutes from 'routes/model'
import ManagerRoutes from 'routes/manager'
import EventEmitter from 'eventemitter3'
import CreatorRoutes from 'routes/creator'
import { popupAlert } from 'components/Popups/helpers'

const authData = getAuthData()
export const authEventEmitter = new EventEmitter<{ logout(): void }>()
export const AuthDataContext = createContext({
  data: authData,
  setAuth: noop,
})

function App() {
  const [auth, setAuth] = useState(authData)
  const apollo = useCreateApollo()
  useEffect(() => {
    authEventEmitter.on('logout', () => {
      cleanAuthData()
      setAuth(null)
      apollo.clearStore()
    })
  }, [])

  return (
    <AuthDataContext.Provider value={useMemo(() => ({ data: auth, setAuth }), [auth])}>
      <BrowserRouter>
        <PageTitle />
        <RedirectByParams />
        <ThemeProvider theme={theme}>
          <ApolloProvider client={apollo}>
            <Popups />
            {!auth ? (
              <GuestRoutes />
            ) : auth.role.toLowerCase() === 'admin' ? (
              <AdminRoutes />
            ) : auth.role.toLowerCase() === 'model' ? (
              <ModelRoutes />
            ) : auth.role.toLowerCase() === 'manager' ? (
              <ManagerRoutes />
            ) : auth.role.toLowerCase() === 'creator' ? (
              <CreatorRoutes />
            ) : (
              <ClientRoutes />
            )}
          </ApolloProvider>
        </ThemeProvider>
      </BrowserRouter>
    </AuthDataContext.Provider>
  )
}

const titleElem = document.head.querySelector('title') as HTMLTitleElement
function setTitle(title: string) {
  titleElem.innerText = title
}

function RedirectByParams() {
  const auth = useContext(AuthDataContext)
  const location = useLocation()

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const accessToken = urlParams.get('access_token')
    const refreshToken = urlParams.get('refresh_token')
    const success = urlParams.get('success')
    const pathname = location.pathname

    if (pathname === '/settings/general' && success === '') {
      if (accessToken !== null && refreshToken !== null) {
        setToken(localStorage, accessToken, refreshToken)
        auth.setAuth(getAuthData(accessToken))
      }

      if (accessToken === null && refreshToken !== null) {
        popupAlert({ type: 'error', message: 'Login error' })
      }

      if (accessToken !== null && refreshToken === null) {
        popupAlert({ type: 'error', message: 'Login error' })
      }
    }
    
  }, [location])
  return null
}

function PageTitle() {
  const location = useLocation()
  const titles: Array<[string | RegExp, string]> = [
    ['/login', 'Login page | Reflekt Me'],
    ['/signup', 'Registration page | Reflekt Me'],
    ['/forgot-password', 'Forgot password page | Reflekt Me'],
    ['/reset-password', 'Reset password page | Reflekt Me'],
    ['/confirm', 'Confirm email page | Reflekt Me'],
    ['/dashboard', 'Dashboard page | Reflekt Me'],
    ['/users/clients', 'Clients page | Reflekt Me'],
    ['/users/models', 'Models page | Reflekt Me'],
    ['/requests/integrations', 'Requests integrations page | Reflekt Me'],
    ['/requests/models', 'Requests models page | Reflekt Me'],
    ['/settings/general', 'General information page | Reflekt Me'],
    ['/settings/body-information', 'Body information page | Reflekt Me'],
    ['/settings/widget', 'Widget settings page | Reflekt Me'],
    ['/products', 'Products page | Reflekt Me'],
    ['/support', 'Support page | Reflekt Me'],
    ['/product', 'Product page | Reflekt Me'],
  ]
  useEffect(() => {
    for (const [template, title] of titles) {
      if (typeof template === 'string' && location.pathname.startsWith(template)) {
        setTitle(title)
        return
      }
      if (template instanceof RegExp && template.test(location.pathname)) {
        setTitle(title)
        return
      }
    }
    setTitle('Reflekt Me')
  }, [location.pathname])
  return null
}

export default App
